// Must-not-miss (can't-miss / life-threat) checklists per chief complaint.
// Each entry: display name + lowercase aliases matched against the group's
// diagnosis texts on whole-word boundaries (see matchesDiagnosis).
// For teaching prompts only, not clinical guidance.

import { matchesAliases } from '../utils/matching';

const MUST_NOT_MISS = {
  'Chest pain': [
    { name: 'Acute coronary syndrome / MI', aliases: ['acs', 'mi', 'stemi', 'nstemi', 'myocardial infarction', 'acute coronary', 'heart attack', 'unstable angina'] },
    { name: 'Aortic dissection', aliases: ['dissection', 'aortic dissection'] },
    { name: 'Pulmonary embolism', aliases: ['pe', 'pulmonary embolism', 'pulmonary embolus'] },
    { name: 'Tension pneumothorax', aliases: ['pneumothorax', 'ptx'] },
    { name: 'Cardiac tamponade', aliases: ['tamponade', 'pericardial effusion'] },
    { name: 'Esophageal rupture (Boerhaave)', aliases: ['boerhaave', 'esophageal rupture', 'oesophageal rupture'] },
  ],
  'Dyspnea': [
    { name: 'Pulmonary embolism', aliases: ['pe', 'pulmonary embolism', 'pulmonary embolus'] },
    { name: 'Tension pneumothorax', aliases: ['pneumothorax', 'ptx'] },
    { name: 'Anaphylaxis', aliases: ['anaphylaxis', 'anaphylactic'] },
    { name: 'Airway obstruction / foreign body', aliases: ['airway obstruction', 'foreign body', 'fbao', 'epiglottitis'] },
    { name: 'Cardiac tamponade', aliases: ['tamponade'] },
    { name: 'Severe asthma / status asthmaticus', aliases: ['status asthmaticus', 'severe asthma'] },
    { name: 'Acute pulmonary edema / heart failure', aliases: ['pulmonary edema', 'pulmonary oedema', 'heart failure', 'chf', 'adhf'] },
    { name: 'Acute coronary syndrome', aliases: ['acs', 'mi', 'myocardial infarction'] },
  ],
  'Headache': [
    { name: 'Subarachnoid hemorrhage', aliases: ['sah', 'subarachnoid'] },
    { name: 'Meningitis / encephalitis', aliases: ['meningitis', 'encephalitis'] },
    { name: 'Raised ICP / mass lesion', aliases: ['icp', 'mass', 'tumor', 'tumour', 'brain mass', 'herniation'] },
    { name: 'Giant cell (temporal) arteritis', aliases: ['gca', 'temporal arteritis', 'giant cell'] },
    { name: 'Cerebral venous sinus thrombosis', aliases: ['cvst', 'venous sinus thrombosis', 'sinus thrombosis'] },
    { name: 'Carbon monoxide poisoning', aliases: ['carbon monoxide', 'co poisoning'] },
    { name: 'Acute angle-closure glaucoma', aliases: ['glaucoma', 'angle closure'] },
    { name: 'Stroke / intracranial hemorrhage', aliases: ['stroke', 'ich', 'intracranial hemorrhage', 'intracerebral'] },
    { name: 'Preeclampsia (if pregnant)', aliases: ['preeclampsia', 'pre-eclampsia', 'eclampsia'] },
  ],
  'Abdominal pain': [
    { name: 'Ruptured AAA', aliases: ['aaa', 'aortic aneurysm'] },
    { name: 'Mesenteric ischemia', aliases: ['mesenteric ischemia', 'mesenteric ischaemia'] },
    { name: 'Perforated viscus', aliases: ['perforation', 'perforated'] },
    { name: 'Ectopic pregnancy', aliases: ['ectopic'] },
    { name: 'Appendicitis', aliases: ['appendicitis'] },
    { name: 'Bowel obstruction', aliases: ['obstruction', 'sbo', 'volvulus'] },
    { name: 'Testicular / ovarian torsion', aliases: ['torsion'] },
    { name: 'Ascending cholangitis', aliases: ['cholangitis'] },
  ],
  'Altered mental status': [
    { name: 'Hypoglycemia', aliases: ['hypoglycemia', 'hypoglycaemia', 'low glucose', 'low blood sugar'] },
    { name: 'Stroke / intracranial hemorrhage', aliases: ['stroke', 'ich', 'intracranial hemorrhage'] },
    { name: 'Meningitis / encephalitis', aliases: ['meningitis', 'encephalitis'] },
    { name: 'Overdose / toxidrome', aliases: ['overdose', 'toxidrome', 'intoxication', 'poisoning'] },
    { name: 'Hypoxia / hypercapnia', aliases: ['hypoxia', 'hypercapnia', 'hypercarbia'] },
    { name: 'Sepsis', aliases: ['sepsis', 'septic'] },
    { name: 'Non-convulsive status epilepticus', aliases: ['status epilepticus', 'ncse'] },
    { name: 'Wernicke encephalopathy', aliases: ['wernicke'] },
  ],
  'Fever': [
    { name: 'Sepsis / septic shock', aliases: ['sepsis', 'septic'] },
    { name: 'Meningitis', aliases: ['meningitis'] },
    { name: 'Necrotizing fasciitis', aliases: ['necrotizing fasciitis', 'necrotising fasciitis', 'nec fasc'] },
    { name: 'Infective endocarditis', aliases: ['endocarditis'] },
    { name: 'Neutropenic fever', aliases: ['neutropenic', 'neutropenia'] },
    { name: 'Malaria (travel history)', aliases: ['malaria'] },
    { name: 'Toxic shock syndrome', aliases: ['toxic shock'] },
  ],
  'Syncope': [
    { name: 'Arrhythmia', aliases: ['arrhythmia', 'vt', 'ventricular tachycardia', 'heart block', 'bradycardia'] },
    { name: 'Pulmonary embolism', aliases: ['pe', 'pulmonary embolism'] },
    { name: 'Aortic stenosis', aliases: ['aortic stenosis'] },
    { name: 'Hypertrophic cardiomyopathy', aliases: ['hocm', 'hypertrophic cardiomyopathy', 'hcm'] },
    { name: 'GI bleed / hypovolemia', aliases: ['gi bleed', 'hemorrhage', 'haemorrhage', 'hypovolemia', 'hypovolaemia'] },
    { name: 'Subarachnoid hemorrhage', aliases: ['sah', 'subarachnoid'] },
    { name: 'Ectopic pregnancy', aliases: ['ectopic'] },
    { name: 'Aortic dissection', aliases: ['dissection'] },
    { name: 'Brugada / long QT', aliases: ['brugada', 'long qt', 'channelopathy'] },
  ],
  'Acute kidney injury / oliguria': [
    { name: 'Obstruction / urinary retention', aliases: ['obstruction', 'obstructive', 'post renal', 'postrenal', 'retention', 'hydronephrosis', 'bph', 'blocked catheter'] },
    { name: 'Hyperkalemia', aliases: ['hyperkalemia', 'hyperkalaemia', 'high potassium'] },
    { name: 'Severe metabolic acidosis', aliases: ['acidosis', 'metabolic acidosis'] },
    { name: 'Uremia / dialysis indication (AEIOU)', aliases: ['uremia', 'uraemia', 'dialysis', 'aeiou', 'rrt', 'renal replacement'] },
    { name: 'Fluid overload / pulmonary edema', aliases: ['overload', 'pulmonary edema', 'pulmonary oedema', 'fluid overload'] },
    { name: 'Sepsis', aliases: ['sepsis', 'septic', 'urosepsis'] },
    { name: 'Rhabdomyolysis', aliases: ['rhabdo', 'rhabdomyolysis'] },
    { name: 'Rapidly progressive glomerulonephritis / vasculitis', aliases: ['rpgn', 'glomerulonephritis', 'vasculitis', 'anca', 'anti gbm', 'goodpasture'] },
    { name: 'Toxic ingestion / intoxication', aliases: ['intoxication', 'ingestion', 'ethylene glycol', 'toxic alcohol', 'overdose'] },
  ],
  'Weakness (neuro)': [
    { name: 'Stroke', aliases: ['stroke', 'cva', 'tia'] },
    { name: 'Cord compression / cauda equina', aliases: ['cord compression', 'cauda equina'] },
    { name: 'Guillain-Barré syndrome', aliases: ['gbs', 'guillain'] },
    { name: 'Myasthenic crisis', aliases: ['myasthenia', 'myasthenic'] },
    { name: 'Hypoglycemia', aliases: ['hypoglycemia', 'hypoglycaemia'] },
    { name: 'Electrolyte / periodic paralysis', aliases: ['hypokalemia', 'hypokalaemia', 'periodic paralysis', 'electrolyte'] },
    { name: 'Botulism', aliases: ['botulism'] },
  ],
};

// Whole-word alias match so "mi" never matches "vomiting".
export const matchesDiagnosis = (diagnosisText, checklistItem) =>
  matchesAliases(diagnosisText, checklistItem.aliases);

export default MUST_NOT_MISS;
